/**
 * Configurações — apenas seções funcionais (Sprint 0 Tier 5a / A7-UX).
 *
 * Audit A7: removidas seções fake (Notificações, Preferências, Segurança,
 * Idioma) cujos toggles/botões não persistiam estado nem disparavam ação.
 * Mantemos apenas BYOK (real) + atalho explícito para gerenciamento de
 * conta via Clerk. Princípio: melhor seção honesta enxuta do que
 * controles que mentem.
 */
import { AppLayout } from "@/components/layout/AppLayout";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ApiKeysSection } from "@/features/byok/components/ApiKeysSection";
import { useUser, useClerk } from "@clerk/clerk-react";
import { Button } from "@/components/ui/button";
import { UserCircle, ShieldCheck, ArrowSquareOut } from "@phosphor-icons/react";

export default function Configuracoes() {
  usePageTitle("Configurações");
  const { hash } = useLocation();
  const { user } = useUser();
  const clerk = useClerk();

  useEffect(() => {
    if (!hash) return;
    const el = document.querySelector(hash);
    if (!el) return;
    setTimeout(() => {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  }, [hash]);

  const openAccountPortal = () => {
    clerk.openUserProfile();
  };

  return (
    <AppLayout>
      <div className="space-y-8 animate-fade-in w-full">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-semibold text-foreground">
            <span className="accent-text">Configurações</span>
          </h1>
          <p className="text-muted-foreground mt-1">
            Gerencie suas chaves de API e segurança da conta
          </p>
        </div>

        {/* API Keys (BYOK) — real, persists to Supabase */}
        <ApiKeysSection />

        {/* Account & Security — delegated to Clerk's hosted portal */}
        <div className="bg-card border border-border/50 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-lg bg-primary/10">
              <ShieldCheck className="w-5 h-5 text-primary" weight="duotone" aria-hidden="true" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">Conta &amp; Segurança</h2>
              <p className="text-sm text-muted-foreground">
                Senha, autenticação em duas etapas, sessões ativas e dados básicos
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3 py-3 border-b border-border/30">
              <div className="flex items-center gap-3 min-w-0">
                <UserCircle className="w-4 h-4 text-muted-foreground shrink-0" aria-hidden="true" />
                <div className="min-w-0">
                  <p className="text-foreground truncate">
                    {user?.primaryEmailAddress?.emailAddress ?? "Conta"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    Acesse o portal seguro do Clerk para alterar senha, ativar 2FA e revisar sessões
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                onClick={openAccountPortal}
                // WCAG 2.5.5 — min 48x48 hit area on mobile.
                className="gap-1.5 shrink-0 min-h-[48px]"
              >
                <span>Abrir portal</span>
                <ArrowSquareOut className="w-3.5 h-3.5" aria-hidden="true" />
              </Button>
            </div>

            <p className="text-xs text-muted-foreground/80 leading-relaxed">
              Para excluir sua conta ou exportar seus dados (LGPD Art. 18), use a aba{" "}
              <a
                href="/perfil"
                // WCAG 2.5.5 — pill hit area on mobile, inline visual on sm+.
                className="inline-flex min-h-[48px] min-w-[48px] items-center px-2 underline hover:text-foreground transition-colors sm:min-h-0 sm:min-w-0 sm:p-0"
              >
                Perfil
              </a>
              .
            </p>
          </div>
        </div>

        <p className="pt-4 text-center text-[11px] text-muted-foreground/60">
          v{__APP_VERSION__}
        </p>
      </div>
    </AppLayout>
  );
}
